import { some } from 'lodash'
import React from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, makeStyles } from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'

import { DraggablePaper, Loading } from 'civic-champs-shared'
import { OutlinedButton, ContainedButton } from 'civic-champs-shared'
import { EditableCheckOut } from '../types'
import { BulkCheckOutTable } from './BulkCheckOutTable'
import useExpectedCheckOutsCollection from '../hooks/useExpectedCheckOutsCollection'
import Alert from '@material-ui/lab/Alert'

interface BulkCheckOutCanceled {
  completed: false
}

interface BulkCheckOutReady {
  completed: true
  checkOuts: EditableCheckOut[]
}

export type BulkCheckOutResult = BulkCheckOutCanceled | BulkCheckOutReady

interface BulkCheckOutPrompt {
  showing: boolean
  complete: (result: BulkCheckOutResult) => void
  checkInIds: number[]
}

const useStyles = makeStyles({
  dialog: {
    background: '#F8FAFF',
    padding: '0 20px 20px',
    position: 'relative',
    maxWidth: '100%',
    width: '895px',
  },
  title: {
    cursor: 'move',
    padding: '20px 0 20px',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '24px',
    color: '#000000',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  closeIcon: {
    fontSize: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    '&:first-child': {
      padding: '10px 0 8px',
    },
  },
  alert: {
    margin: '12px 0 12px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0 0',
  },
})

export function BulkCheckOutPrompt({ showing, complete, checkInIds }: BulkCheckOutPrompt) {
  const classes = useStyles()

  const [{ checkOuts, loading }, { editCheckOut }] = useExpectedCheckOutsCollection(checkInIds)
  const missingRoles = some(checkOuts, checkOut => !checkOut.role)

  const handleClose = () => {
    complete({ completed: false })
  }

  const handleSave = () => {
    complete({
      completed: true,
      checkOuts: checkOuts || [],
    })
  }

  return (
    <Dialog
      open={showing}
      PaperComponent={DraggablePaper}
      // @ts-ignore
      PaperProps={{ handle: '#bulk-checkout-prompt' }}
      maxWidth="xs"
      classes={{ paper: classes.dialog }}
      disableEnforceFocus
    >
      <DialogTitle id="bulk-checkout-prompt" classes={{ root: classes.title }} disableTypography={true}>
        Finalize Volunteer Check-Out Details
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </DialogTitle>
      {loading ? (
        <>
          <DialogContent>
            <Loading />
          </DialogContent>
          <DialogActions className={classes.actions}>
            <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
            <ContainedButton disabled={true}>Check Out</ContainedButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent className={classes.content}>
            <BulkCheckOutTable checkOuts={checkOuts} onEdit={editCheckOut} />
            {missingRoles && (
              <Alert classes={{ root: classes.alert }} severity="warning">
                Some volunteers have not been assigned a role. Their activities will not have a role after check-out.
              </Alert>
            )}
          </DialogContent>
          <DialogActions className={classes.actions}>
            <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
            <ContainedButton onClick={handleSave}>Check Out</ContainedButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
